import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/ui-kit";
import { formatINR, formatNum } from "@/lib/format";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";

const COLORS = ["#FFB800","#09090B","#3F3F46","#10B981","#3B82F6","#F59E0B","#EF4444"];

export default function Reports() {
  const [data, setData] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  useEffect(() => {
    Promise.all([api.get("/owner/reports/summary"), api.get("/owner/vehicles")])
      .then(([r, v]) => { setData(r.data); setVehicles(v.data); });
  }, []);

  if (!data) return <div className="text-sm text-zinc-500">Loading…</div>;

  const byType = data.by_booking_type.map((r) => ({ name: r._id || "Unknown", count: r.count, revenue: r.revenue }));
  const expCat = data.expense_by_category.map((r) => ({ name: r._id || "Other", total: r.total }));
  const util = data.vehicle_utilization.map((r) => {
    const v = vehicles.find((x) => x.id === r._id);
    return { name: v?.registration_number || "Unknown", trips: r.trips, revenue: r.revenue };
  });

  return (
    <div data-testid="reports-page">
      <PageHeader title="Reports" subtitle="Operational and financial performance at a glance." />
      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        <Card title="Bookings by Type">
          {byType.length === 0 ? <EmptyMsg /> : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={byType}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                <XAxis dataKey="name" stroke="#71717a" fontSize={12} />
                <YAxis stroke="#71717a" fontSize={12} />
                <Tooltip />
                <Bar dataKey="count" fill="#FFB800" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
        <Card title="Expenses by Category">
          {expCat.length === 0 ? <EmptyMsg /> : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={expCat} dataKey="total" nameKey="name" outerRadius={90} label={(d) => `${d.name}`}>
                  {expCat.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => formatINR(v)} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>
      <Card title="Vehicle Utilisation (Completed Trips)">
        {util.length === 0 ? <EmptyMsg /> : (
          <div className="overflow-x-auto">
            <table className="tbl">
              <thead><tr><th>Vehicle</th><th>Completed Trips</th><th>Revenue</th></tr></thead>
              <tbody>
                {util.map((u, i) => (
                  <tr key={i}>
                    <td className="font-mono">{u.name}</td>
                    <td>{formatNum(u.trips)}</td>
                    <td className="font-mono">{formatINR(u.revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-6">
      <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mb-1">— Report</div>
      <div className="font-display font-bold text-xl text-asphalt mb-4">{title}</div>
      {children}
    </div>
  );
}
function EmptyMsg() {
  return <div className="text-sm text-zinc-500">No data yet.</div>;
}
